import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface SentryIssue {
  id: string;
  title: string;
  culprit: string;
  shortId: string;
  count: string;
  userCount: number;
  firstSeen: string;
  lastSeen: string;
  level: "error" | "warning" | "info" | "fatal";
  status: "resolved" | "unresolved" | "ignored";
  permalink: string;
  project: {
    id: string;
    name: string;
    slug: string;
  };
  metadata?: {
    value?: string;
    type?: string;
    filename?: string;
    function?: string;
  };
}

export type StatsPeriod = "24h" | "7d" | "14d" | "30d";

export function useSentryIssues(statsPeriod: StatsPeriod = "24h") {
  return useQuery({
    queryKey: ['sentry-issues', statsPeriod],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke('get-sentry-issues', {
        body: {},
        headers: {},
      });
      
      // The function uses query params, but invoke passes body
      // We need to call with the statsPeriod as a query param
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-sentry-issues?statsPeriod=${statsPeriod}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch Sentry issues');
      }

      const issues = await response.json();
      return issues as SentryIssue[];
    },
    staleTime: 60 * 1000, // 1 minute
    refetchOnWindowFocus: false,
  });
}
